import { appApi, idTag, orgTag, studentTag } from '@/app/redux/appApi';
import {
  CreateLearnerNoteInput,
  LearnerNote,
  UpdateLearnerNoteInput,
} from '@/common/types/learnerNote';
import api from '@/db/apiClient';

const learnerNotesApi = appApi.injectEndpoints({
  endpoints: (build) => ({
    getLearnerNote: build.query<LearnerNote | null, string>({
      query: (learnerNoteId) =>
        api.asEducator.getLearnerNote(learnerNoteId),
      providesTags: (_res, _err, id) => [idTag('LearnerNotes', id)],
    }),

    getLearnerNotesForStudent: build.query<
      LearnerNote[],
      { orgId: string; studentId: string }
    >({
      query: ({ orgId, studentId }) =>
        api.asEducator.getLearnerNotesForStudent(orgId, studentId),
      providesTags: (result, _err, { orgId, studentId }) =>
        result
          ? [
              orgTag('LearnerNotes', orgId),
              studentTag('LearnerNotes', studentId),
              ...result.map((lp) => idTag('LearnerNotes', lp.id)),
            ]
          : [
              orgTag('LearnerNotes', orgId),
              studentTag('LearnerNotes', studentId),
            ],
    }),

    createLearnerNote: build.mutation<LearnerNote, CreateLearnerNoteInput>({
      query: (data) => api.asEducator.createLearnerNote(data),
      invalidatesTags: (_res, _err, data) => [
        orgTag('LearnerNotes', data.organisationId),
      ],
    }),

    updateLearnerNote: build.mutation<
      null,
      {
        orgId: string;
        studentId: string;
        id: string;
        updates: UpdateLearnerNoteInput;
      }
    >({
      query: async ({ id, updates }) => {
        await api.asEducator.updateLearnerNote(id, updates);
        return null;
      },
      onQueryStarted: async (
        { orgId, studentId, id, updates },
        { dispatch, queryFulfilled },
      ) => {
        const patch = dispatch(
          learnerNotesApi.util.updateQueryData(
            'getLearnerNote',
            id,
            (draft) => {
              if (draft) Object.assign(draft, updates, { updatedAt: Date.now() });
            },
          ),
        );
        const studentPatch = dispatch(
          learnerNotesApi.util.updateQueryData(
            'getLearnerNotesForStudent',
            { orgId, studentId },
            (draft) => {
              const lp = draft.find((x) => x.id === id);
              if (lp) Object.assign(lp, updates, { updatedAt: Date.now() });
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
          studentPatch.undo();
        }
      },
    }),

    deleteLearnerNote: build.mutation<
      null,
      { orgId: string; studentId: string; id: string }
    >({
      query: async ({ id }) => {
        await api.asEducator.deleteLearnerNote(id);
        return null;
      },
      onQueryStarted: async (
        { orgId, studentId, id },
        { dispatch, queryFulfilled },
      ) => {
        const patch = dispatch(
          learnerNotesApi.util.updateQueryData(
            'getLearnerNote',
            id,
            () => null,
          ),
        );
        const studentPatch = dispatch(
          learnerNotesApi.util.updateQueryData(
            'getLearnerNotesForStudent',
            { orgId, studentId },
            (draft) => draft.filter((note) => note.id !== id),
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
          studentPatch.undo();
        }
      },
    }),
  }),

  overrideExisting: false,
});

export default learnerNotesApi;
export const {
  useGetLearnerNoteQuery,
  useGetLearnerNotesForStudentQuery,
  useCreateLearnerNoteMutation,
  useUpdateLearnerNoteMutation,
  useDeleteLearnerNoteMutation,
} = learnerNotesApi;
